import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSpinner } from '@fortawesome/free-solid-svg-icons';
import ModalOverlay from '../../../components/ui/ModalOverlay/ModalOverlay';
import DatePicker from '../../../components/ui/DatePicker/DatePicker';
import s from '../CaseFilePage.module.css';

interface TaskFormState {
  assigned_to: string;
  description: string;
  deadline: string;
  deadlineHour: string;
  deadlineMinute: string;
  allow_reminders: boolean;
  reminderDate: string;
  reminderHour: string;
  reminderMinute: string;
  category: string;
}

interface Solicitor {
  cognito_user_id?: string;
  username: string;
  displayName?: string;
  display_name?: string;
}

interface CreateTaskModalProps {
  taskForm: TaskFormState;
  setTaskForm: React.Dispatch<React.SetStateAction<TaskFormState>>;
  handleCreateTask: () => void;
  solicitors: Solicitor[];
  isPending: boolean;
  onClose: () => void;
}

export default function CreateTaskModal({
  taskForm,
  setTaskForm,
  handleCreateTask,
  solicitors,
  isPending,
  onClose,
}: CreateTaskModalProps) {
  return (
    <ModalOverlay onClose={onClose} title="Create Task">
      <div className={s.modalForm}>
        <div className={s.formGroup}>
          <label>Assign To</label>
          <select
            value={taskForm.assigned_to}
            onChange={(e) => setTaskForm((p) => ({ ...p, assigned_to: e.target.value }))}
          >
            <option value="">Select…</option>
            {solicitors.map((sol) => (
              <option
                key={sol.cognito_user_id || sol.username}
                value={sol.cognito_user_id || sol.username}
              >
                {sol.displayName || sol.display_name || sol.username}
              </option>
            ))}
          </select>
        </div>
        <div className={s.formGroup}>
          <label>Description</label>
          <textarea
            rows={3}
            value={taskForm.description}
            onChange={(e) => setTaskForm((p) => ({ ...p, description: e.target.value }))}
            placeholder="Task description…"
          />
        </div>
        <div className={s.formGroup}>
          <label>Category</label>
          <select
            value={taskForm.category}
            onChange={(e) => setTaskForm((p) => ({ ...p, category: e.target.value }))}
          >
            <option value="admin">Admin</option>
            <option value="client">Client</option>
            <option value="court">Court</option>
            <option value="defendant">Defendant</option>
            <option value="misc">Miscellaneous</option>
          </select>
        </div>
        <div className={s.formRow}>
          <div className={s.formGroup}>
            <label>Deadline Date</label>
            <DatePicker
              value={taskForm.deadline}
              onChange={(v) => setTaskForm((p) => ({ ...p, deadline: v }))}
            />
          </div>
          <div className={s.formGroup}>
            <label>Hour</label>
            <select
              value={taskForm.deadlineHour}
              onChange={(e) => setTaskForm((p) => ({ ...p, deadlineHour: e.target.value }))}
            >
              {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          </div>
          <div className={s.formGroup}>
            <label>Minute</label>
            <select
              value={taskForm.deadlineMinute}
              onChange={(e) => setTaskForm((p) => ({ ...p, deadlineMinute: e.target.value }))}
            >
              {['00', '15', '30', '45'].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className={s.formGroup}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={taskForm.allow_reminders}
              onChange={(e) => setTaskForm((p) => ({ ...p, allow_reminders: e.target.checked }))}
            />
            Enable Reminder
          </label>
        </div>
        {taskForm.allow_reminders && (
          <div className={s.formRow}>
            <div className={s.formGroup}>
              <label>Reminder Date</label>
              <DatePicker
                value={taskForm.reminderDate}
                onChange={(v) => setTaskForm((p) => ({ ...p, reminderDate: v }))}
              />
            </div>
            <div className={s.formGroup}>
              <label>Hour</label>
              <select
                value={taskForm.reminderHour}
                onChange={(e) => setTaskForm((p) => ({ ...p, reminderHour: e.target.value }))}
              >
                {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </div>
            <div className={s.formGroup}>
              <label>Minute</label>
              <select
                value={taskForm.reminderMinute}
                onChange={(e) => setTaskForm((p) => ({ ...p, reminderMinute: e.target.value }))}
              >
                {['00', '15', '30', '45'].map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
        <div className={s.modalActions}>
          <button className={s.btnSecondary} onClick={onClose}>
            Cancel
          </button>
          <button
            className={s.btnPrimary}
            onClick={handleCreateTask}
            disabled={!taskForm.description.trim() || !taskForm.assigned_to || isPending}
          >
            {isPending ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faPlus} />
            )}
            Create Task
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
